/**
 * Lightweight runtime validation at the kanban FE/BE boundary.
 * ============================================================
 * Arc 11 / audit P2-12 — "no runtime schema validation at FE/BE
 * boundary (every `as T` lies)".
 *
 * Goal
 * ----
 * Catch deploy-skew between the FE bundle and the BE schema BEFORE
 * the offending value reaches a render path that crashes (e.g. a
 * `Card.title.toUpperCase()` on `null`). The audit's worry was that
 * every `as Card` cast in `api.fetch.ts` is a lie that the
 * TypeScript compiler can't detect — when the BE adds a new
 * required column or removes a column the FE expects, the next
 * render is a `TypeError: Cannot read properties of undefined`
 * with no breadcrumb pointing at the network response.
 *
 * Design
 * ------
 * We could pull in zod, but that's a 12 KB peer-dep cost on every
 * consumer. Instead, this file ships a hand-rolled, dependency-
 * free `Schema<T>` that:
 *
 *   - Validates required vs optional keys.
 *   - Validates primitive types (string / number / boolean / null /
 *     array-of / record / id).
 *   - On failure, calls `onValidationFailure` (defaults to
 *     `console.warn` in dev, no-op in prod) so the page still
 *     renders the response — we don't want a missing field on a
 *     non-critical column to white-screen the operator.
 *   - Optionally hands the violations to `window.Sentry` if
 *     present, so production deploys generate one breadcrumb
 *     per skew event without a fatal error.
 *
 * Wiring
 * ------
 * `api.fetch.ts` imports `validateOrWarn(schema, value)` and wraps
 * the JSON body of the most user-facing endpoints (board list,
 * board detail, lane list, card list, card detail). Mutations are
 * NOT validated — they're the operator's input shape, not the
 * BE's response shape.
 */

export type Schema<_T> = {
  parse: (value: unknown, path?: string) => SchemaResult
}

export interface SchemaResult {
  ok: boolean
  errors: SchemaError[]
}

export interface SchemaError {
  path: string
  expected: string
  received: string
}

export type FailureSink = (
  endpoint: string,
  errors: SchemaError[],
) => void

let _failureSink: FailureSink = (endpoint, errors) => {
  if (typeof console === 'undefined') return
  if (errors.length === 0) return
  // eslint-disable-next-line no-console
  console.warn(
    `[kanban-validation] schema mismatch on ${endpoint}: ` +
      errors
        .slice(0, 5)
        .map(
          (e) => `${e.path}: expected ${e.expected}, got ${e.received}`,
        )
        .join('; ') +
      (errors.length > 5 ? ` (+${errors.length - 5} more)` : ''),
  )
  try {
    const w = (globalThis as any).window
    const Sentry = w?.Sentry
    if (Sentry?.captureMessage) {
      Sentry.captureMessage(
        `kanban schema mismatch on ${endpoint}`,
        {
          level: 'warning',
          extra: { errors: errors.slice(0, 25), endpoint },
        },
      )
    }
  } catch {
    /* noop */
  }
}

const _defaultSink = _failureSink

export function setKanbanValidationSink(sink: FailureSink | null) {
  // Arc 12: allow `null` to mean "restore default" so tests can
  // safely clean up after themselves without leaking sinks across
  // describe blocks.
  _failureSink = sink ?? _defaultSink
}

export function validateOrWarn<T>(
  schema: Schema<T>,
  value: unknown,
  endpoint: string,
): T {
  const result = schema.parse(value)
  if (!result.ok) {
    _failureSink(endpoint, result.errors)
  }
  return value as T
}

// ---------- primitives ----------

function describe(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

export const Str: Schema<string> = {
  parse: (value, path = '') => ({
    ok: typeof value === 'string',
    errors:
      typeof value === 'string'
        ? []
        : [{ path, expected: 'string', received: describe(value) }],
  }),
}

export const Num: Schema<number> = {
  parse: (value, path = '') => ({
    ok: typeof value === 'number' && !Number.isNaN(value),
    errors:
      typeof value === 'number' && !Number.isNaN(value)
        ? []
        : [{ path, expected: 'number', received: describe(value) }],
  }),
}

export const Bool: Schema<boolean> = {
  parse: (value, path = '') => ({
    ok: typeof value === 'boolean',
    errors:
      typeof value === 'boolean'
        ? []
        : [{ path, expected: 'boolean', received: describe(value) }],
  }),
}

export const Id: Schema<string | number> = {
  parse: (value, path = '') => {
    // Arc 12: tighten — empty strings and non-positive integers
    // are NOT valid identifiers (the prior accept-all behaviour
    // let `''` and `0` slip through, and the React Query queryKey
    // builders happily mint `['kanban','card','']` cache slices).
    if (typeof value === 'string' && value.length > 0) {
      return { ok: true, errors: [] }
    }
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
      return { ok: true, errors: [] }
    }
    return {
      ok: false,
      errors: [
        { path, expected: 'non-empty string OR positive integer (id)', received: describe(value) },
      ],
    }
  },
}

export function arrayOf<T>(item: Schema<T>): Schema<T[]> {
  return {
    parse: (value, path = '') => {
      if (!Array.isArray(value)) {
        return {
          ok: false,
          errors: [{ path, expected: 'array', received: describe(value) }],
        }
      }
      const errors: SchemaError[] = []
      value.forEach((entry, idx) => {
        const r = item.parse(entry, `${path}[${idx}]`)
        if (!r.ok) errors.push(...r.errors)
      })
      return { ok: errors.length === 0, errors }
    },
  }
}

export function objectOf(
  required: Record<string, Schema<any>>,
  optional: Record<string, Schema<any>> = {},
): Schema<Record<string, unknown>> {
  return {
    parse: (value, path = '') => {
      if (
        value === null ||
        typeof value !== 'object' ||
        Array.isArray(value)
      ) {
        return {
          ok: false,
          errors: [{ path, expected: 'object', received: describe(value) }],
        }
      }
      const errors: SchemaError[] = []
      const v = value as Record<string, unknown>
      for (const [key, schema] of Object.entries(required)) {
        if (!(key in v)) {
          errors.push({
            path: path ? `${path}.${key}` : key,
            expected: 'present',
            received: 'undefined',
          })
          continue
        }
        const r = schema.parse(v[key], path ? `${path}.${key}` : key)
        if (!r.ok) errors.push(...r.errors)
      }
      for (const [key, schema] of Object.entries(optional)) {
        if (!(key in v)) continue
        if (v[key] === null || v[key] === undefined) continue
        const r = schema.parse(v[key], path ? `${path}.${key}` : key)
        if (!r.ok) errors.push(...r.errors)
      }
      return { ok: errors.length === 0, errors }
    },
  }
}

// ---------- canonical kanban schemas ----------

export const BoardSchema = objectOf(
  {
    id: Id,
    name: Str,
  },
  {
    kind: Str,
    organisation: Id,
    archived: Bool,
    visibility: Str,
    job_id: Num,
  },
)

export const LaneSchema = objectOf(
  {
    id: Id,
    name: Str,
    position: Num,
  },
  {
    kind: Str,
    board: Id,
    archived: Bool,
    wip_limit: Num,
  },
)

export const CardSchema = objectOf(
  {
    id: Id,
    title: Str,
  },
  {
    summary: Str,
    status: Str,
    lane: Id,
    organisation: Id,
    position: Num,
    updated_at: Str,
    job_id: Num,
    primary_email: Id,
    confidence: Num,
    comments_count: Num,
  },
)

export const BoardListSchema = arrayOf(BoardSchema)
export const LaneListSchema = arrayOf(LaneSchema)
export const CardListSchema = arrayOf(CardSchema)
